import React, { useState } from "react";

import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import useUpdateEffect from "hooks/useUpdateEffect";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";

import "../css/createTikTokDestination.css";
import {
    getPinterestAuthUrl,
    pinterestAuthorize,
} from "../helpers/destinations/createDestination";
import LoginButton from "./molecules/LoginButton";
import SwalAlertComp from "./SwalAlert";

const PinterestAuthAndSelectPixels = ({
    createPixelStatus,
    destinationType,
    setPinterestFormStatus,
    setIsPinterestAuthButtonVisible,
    setPinterestCapiData,
    setIsSaveReady,
    setIsAuthorised,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [authPixels, setAuthPixels] = useState([]);
    const [authButtonVisible, setAuthButtonVisible] = useState(true);
    const [selectedAdvertiserId, setSelectedAdvertiserId] = useState("");
    const [selectedAdvertiserName, setSelectedAdvertiserName] = useState("");
    const [selectedAdvertiserPixelId, setSelectedAdvertiserPixelId] =
        useState("");
    const [selectedAdvertiserPixelName, setSelectedAdvertiserPixelName] =
        useState("");
    const [selectedAdvertiserPixels, setSelectedAdvertiserPixels] = useState(
        [],
    );

    const language = useSelector((state) => state.languagesState.languages);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const pinterestAuthorizeCall = async (data) => {
        setIsLoading(true);
        let response = await pinterestAuthorize(data);
        if (response.status) {
            if (setPinterestFormStatus !== undefined) {
                setPinterestFormStatus(true);
            }
            setAuthPixels(response?.data?.pixels);
            setAuthButtonVisible(false);
            setIsAuthorised?.(true);
            setIsPinterestAuthButtonVisible?.(false);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
        }
        setIsLoading(false);
    };

    const handleAdvertiserId = (event) => {
        setSelectedAdvertiserId(event.target.value);
        setSelectedAdvertiserPixelId("");
        setSelectedAdvertiserPixelName("");
        let selectedItem = [...authPixels].find(
            (x) => x.advertiser_id === event.target.value,
        );
        if (selectedItem !== undefined) {
            setSelectedAdvertiserName(selectedItem?.advertiser_name);
            if (selectedItem?.pixels.length > 0) {
                setSelectedAdvertiserPixels(selectedItem?.pixels);
            } else {
                setSelectedAdvertiserPixels([]);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        language?.destination_modal?.pinterest_pixel_error ??
                        "The Pixel for the Advertiser you selected could not be found.",
                    icon: "error",
                });
            }
        }
    };

    const handleAdvertiserPixelId = (event) => {
        setSelectedAdvertiserPixelId(event.target.value);
        let selectedItem = [...selectedAdvertiserPixels].find(
            (x) => x.pixel_id === event.target.value,
        );
        if (selectedItem !== undefined) {
            setSelectedAdvertiserPixelName(selectedItem?.pixel_name);
        }
    };

    useUpdateEffect(() => {
        const isReady = Boolean(
            selectedAdvertiserName &&
            selectedAdvertiserPixelId &&
            selectedAdvertiserId,
        );
        setIsSaveReady?.(isReady);

        const pixels = [
            {
                advertiser_name: selectedAdvertiserName,
                advertiser_id: selectedAdvertiserId,
                pixel_id: selectedAdvertiserPixelId,
                pixel_name: selectedAdvertiserPixelName,
                type: destinationType ?? "",
            },
        ];

        setPinterestCapiData?.({
            pixels,
            create_pixel:
                createPixelStatus !== undefined ? createPixelStatus : false,
        });
    }, [
        selectedAdvertiserName,
        selectedAdvertiserId,
        selectedAdvertiserPixelId,
        selectedAdvertiserPixelName,
        destinationType,
        createPixelStatus,
    ]);

    return (
        <div className="w-full">
            {authButtonVisible ? (
                <>
                    <div className="position-relative overflow-hidden z-1 mb-3">
                        <LoginButton
                            code="pinterest"
                            getAuthUrl={getPinterestAuthUrl}
                            getAuthUrlResponseTransform={(response) => {
                                return response?.data?.url;
                            }}
                            paramNames={["code", "state"]}
                            onSuccess={pinterestAuthorizeCall}
                            dataTransform={(params) => ({
                                code: params.code,
                                state: params.state,
                            })}
                            logoSrc="/assets/images/typeImages/pinterest-icon.png"
                            buttonText={
                                language?.destination_modal
                                    ?.continue_with_pinterest ??
                                "Continue with Pinterest"
                            }
                            isLoadingExternal={isLoading}
                        />
                    </div>

                    {authButtonVisible && (
                        <span className="d-flex justify-content-center">
                            {language?.lead?.or ?? "OR"}
                        </span>
                    )}
                </>
            ) : (
                <div>
                    {authPixels && authPixels.length > 0 && (
                        <div className="mb-3">
                            <Form.Group controlId="exampleForm.ControlInput1">
                                <FormControl style={{ width: "100%" }}>
                                    <Select
                                        label={
                                            language?.destination_modal
                                                ?.advertisers ?? "Advertisers"
                                        }
                                        value={selectedAdvertiserId}
                                        onChange={handleAdvertiserId}
                                        MenuProps={MenuProps}
                                    >
                                        {authPixels &&
                                            authPixels.map((item) => (
                                                <MenuItem
                                                    key={item?.advertiser_id}
                                                    value={item?.advertiser_id}
                                                >
                                                    {item?.advertiser_name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Form.Group>
                        </div>
                    )}
                    {selectedAdvertiserPixels &&
                        selectedAdvertiserPixels.length > 0 && (
                            <div className="mb-3">
                                <Form.Group controlId="exampleForm.ControlInput1">
                                    <FormControl style={{ width: "100%" }}>
                                        <Select
                                            label={
                                                language?.destination_modal
                                                    ?.pixels ?? "Pixels"
                                            }
                                            value={selectedAdvertiserPixelId}
                                            onChange={handleAdvertiserPixelId}
                                            MenuProps={MenuProps}
                                        >
                                            {selectedAdvertiserPixels &&
                                                selectedAdvertiserPixels.map(
                                                    (item) => (
                                                        <MenuItem
                                                            key={item?.pixel_id}
                                                            value={
                                                                item?.pixel_id
                                                            }
                                                        >
                                                            {item?.pixel_name}
                                                        </MenuItem>
                                                    ),
                                                )}
                                        </Select>
                                    </FormControl>
                                </Form.Group>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
};

export default PinterestAuthAndSelectPixels;
